import {createShopifyImageUrl} from '@/utilities/create-shopify-image-url';
import {preloadImage} from '@/utilities/preload-image';
import {ProductState, VariantState} from './types';

export type PreloadProductImageCarouselInput = {
  product: ProductState;
  selectedVariant: VariantState | null;
};

/**
 * Preloads the images for a product image carousel.
 * These are the images for the selected variant, or the product's featured image if no variant is selected.
 * The images are returned to be used in the carousel.
 *
 * @param product - The product object.
 * @param selectedVariant - The selected variant object.
 * @returns - The preloaded images for the product carousel.
 */
export function preloadProductCarouselImages({
  product,
  selectedVariant,
}: PreloadProductImageCarouselInput) {
  const images = selectedVariant?.images ?? [];
  const featured =
    images[0] ?? selectedVariant?.image ?? product?.featuredImage;

  if (images.length === 0) {
    images.push(featured);
  }

  // Preload the featured image for each variant, if available
  // This makes the user experience smoother when selecting the variant
  product?.variants.forEach(preloadVariantImage);

  return images;
}

/**
 * Preloads the 'featured' image of a variant.
 * This is either the first image in the variant's images array or the variant's image.
 *
 * @param variant - The variant object containing the image information.
 */
function preloadVariantImage(variant: VariantState) {
  const featured = variant.images[0] ?? variant.image;
  if (!featured) return;

  preloadImage(createShopifyImageUrl(featured.url, 640));
}
